import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslationChange } from 'i18nano'
import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import {
	adminTrpcClient,
	uploadLotPhoto,
	useAdminTrpc,
	type AdminOutputs,
} from 'shared/api'
import { MultiImageDropzone } from 'shared/components/ui/multi-image-dropzone'
import { formatUzsAmount } from 'shared/lib/format'
import { getLocalizedCategoryName } from 'shared/lib/categories'
import { useTranslate } from 'shared/lib/i18n'
import { calculateRecommendedPriceUzs } from '../lib/calculate-recommended-price'
import { expandSizes } from '../lib/expand-sizes'
import { Button } from 'shared/components/ui/button'
import { Field, FieldDescription, FieldLabel } from 'shared/components/ui/field'
import { Input } from 'shared/components/ui/input'
import { Textarea } from 'shared/components/ui/textarea'

type LotPhoto = { key: string; url: string }

type InventoryType = 'slots' | 'stock'
type StockRow = { tempId: string; name: string; maxQuantity: string }
type PriceCalculation = AdminOutputs['lots']['Calculate']

function makeTempId() {
	return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function CreateLot() {
	const trpc = useAdminTrpc()
	const navigate = useNavigate()
	const create = useMutation(trpc.lots.Create.mutationOptions())
	const { t, prefixT } = useTranslate('pages.adminCreateLot')
	const { lang } = useTranslationChange()

	const { data: categories } = useQuery(trpc.categories.List.queryOptions())
	const [categoryIds, setCategoryIds] = useState<string[]>([])

	function toggleCategory(id: string) {
		setCategoryIds((ids) =>
			ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
		)
	}

	const [title, setTitle] = useState('')
	const [photos, setPhotos] = useState<LotPhoto[]>([])
	const [uploadingCount, setUploadingCount] = useState(0)

	function handlePhotos(files: File[]) {
		setUploadingCount((n) => n + files.length)
		for (const file of files) {
			// Each file settles independently: successes append as they finish.
			void uploadLotPhoto(file)
				.then((photo) => setPhotos((current) => [...current, photo]))
				.catch(() => toast.error(prefixT('photoUploadFailed')))
				.finally(() => setUploadingCount((n) => n - 1))
		}
	}
	const [notes, setNotes] = useState('')
	const [description, setDescription] = useState('')
	const [storeUrl, setStoreUrl] = useState('')
	const [priceUsd, setPriceUsd] = useState('')
	const [priceUzs, setPriceUzs] = useState('')
	const [priceCalculation, setPriceCalculation] =
		useState<PriceCalculation | null>(null)
	const [priceCorrectionVersion, setPriceCorrectionVersion] = useState(0)
	const priceUsdRef = useRef('')
	const initialPriceCalculationRef = useRef<Promise<PriceCalculation> | null>(
		null,
	)
	const priceCalculationRequestRef = useRef(0)

	// inventory.type is chosen at creation and is immutable afterwards.
	const [inventoryType, setInventoryType] = useState<InventoryType>('slots')
	const [sizesRaw, setSizesRaw] = useState('')
	const [stockRows, setStockRows] = useState<StockRow[]>(() => [
		{ tempId: makeTempId(), name: '', maxQuantity: '' },
	])

	function updateStockRow(
		tempId: string,
		patch: Partial<Omit<StockRow, 'tempId'>>,
	) {
		setStockRows((rows) =>
			rows.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
		)
	}

	function addStockRow() {
		setStockRows((rows) => [
			...rows,
			{ tempId: makeTempId(), name: '', maxQuantity: '' },
		])
	}

	function removeStockRow(tempId: string) {
		setStockRows((rows) => rows.filter((row) => row.tempId !== tempId))
	}

	const [buyerTezId, setBuyerTezId] = useState('')
	const [buyerName, setBuyerName] = useState<string | null>(null)
	const [buyerNotFound, setBuyerNotFound] = useState(false)
	const [lookingUp, setLookingUp] = useState(false)
	// The exact value we last ran a lookup for, so neither an explicit search
	// (Enter / кнопка) nor the on-blur fallback re-fetches an already-checked
	// value. Cleared on every edit (see the input's onChange).
	const lastLookedUpRef = useRef<string | null>(null)

	async function lookupBuyer() {
		const tezId = buyerTezId.trim()
		if (!tezId) {
			setBuyerName(null)
			setBuyerNotFound(false)
			lastLookedUpRef.current = null
			return
		}
		// Skip if this exact value was already looked up (avoids a duplicate query
		// when the admin clicks "Найти" — which blurs the input first — and when a
		// resolved buyer is already shown).
		if (lastLookedUpRef.current === tezId) return
		lastLookedUpRef.current = tezId
		setLookingUp(true)
		try {
			const user = await adminTrpcClient.users.FindByTezId.query({ tezId })
			setBuyerName(user.name)
			setBuyerNotFound(false)
		} catch {
			setBuyerName(null)
			setBuyerNotFound(true)
		} finally {
			setLookingUp(false)
		}
	}

	// Split sizes by comma and expand patterns such as "1x10" into ten slots.
	const sizes = useMemo(() => expandSizes(sizesRaw), [sizesRaw])

	// Stock rows with a name; empty rows are ignored the way empty size parts are.
	const stockItems = useMemo(
		() =>
			stockRows
				.map((row) => ({
					name: row.name.trim(),
					maxQuantity: Number(row.maxQuantity),
				}))
				.filter((row) => row.name),
		[stockRows],
	)
	// Every named row must carry a positive integer quantity.
	const hasInvalidStock = stockRows.some((row) => {
		if (!row.name.trim()) return false
		const qty = Number(row.maxQuantity)
		return !Number.isInteger(qty) || qty < 1
	})
	const inventoryValid =
		inventoryType === 'slots'
			? sizes.length > 0
			: stockItems.length > 0 && !hasInvalidStock

	useEffect(() => {
		let active = true
		const requestId = ++priceCalculationRequestRef.current
		const request =
			initialPriceCalculationRef.current ??
			(initialPriceCalculationRef.current =
				adminTrpcClient.lots.Calculate.query({
					priceUsd: 0,
				}))

		void request
			.then((calculation) => {
				if (active && requestId === priceCalculationRequestRef.current) {
					setPriceCalculation(calculation)
				}
			})
			.catch(() => {
				// The blur request can still obtain pricing if the initial request failed.
			})

		return () => {
			active = false
		}
	}, [])

	const usd = Number(priceUsd)
	const recommendedPriceUzs =
		priceCalculation && usd > 0
			? calculateRecommendedPriceUzs(
					usd,
					priceCalculation.rate,
					priceCalculation.commissionPercent,
				)
			: undefined

	async function reconcileRecommendedPrice() {
		const requestedPriceUsd = priceUsdRef.current
		const requestedUsd = Number(requestedPriceUsd)
		if (!(requestedUsd > 0)) return

		const localPriceUzs = priceCalculation
			? calculateRecommendedPriceUzs(
					requestedUsd,
					priceCalculation.rate,
					priceCalculation.commissionPercent,
				)
			: undefined
		const requestId = ++priceCalculationRequestRef.current
		try {
			const serverCalculation = await adminTrpcClient.lots.Calculate.query({
				priceUsd: requestedUsd,
			})
			if (
				requestId !== priceCalculationRequestRef.current ||
				priceUsdRef.current !== requestedPriceUsd
			) {
				return
			}

			setPriceCalculation(serverCalculation)
			if (
				localPriceUzs !== undefined &&
				localPriceUzs !== serverCalculation.priceUzs
			) {
				setPriceCorrectionVersion((version) => version + 1)
			}
		} catch {
			// Keep showing the local estimate until a later blur reaches the server.
		}
	}

	async function handleCreate() {
		try {
			const lot = await create.mutateAsync({
				title,
				photoKeys: photos.map((photo) => photo.key),
				categoryIds,
				notes: notes || undefined,
				description: description.trim() || undefined,
				storeUrl: storeUrl.trim() || undefined,
				priceUsd: usd,
				priceUzs: priceUzs ? Number(priceUzs) : undefined,
				inventory:
					inventoryType === 'slots'
						? { type: 'slots', names: sizes }
						: { type: 'stock', items: stockItems },
				initiatorTezId: buyerTezId.trim() || undefined,
			})
			toast.success(prefixT('created'))
			navigate('/admin/lots')
			void lot
		} catch (e) {
			toast.error(
				e instanceof Error && e.message.includes('MINIAPP_URL_NOT_CONFIGURED')
					? prefixT('miniAppUrlNotConfigured')
					: e instanceof Error && e.message.includes('INITIATOR_NO_TELEGRAM')
						? prefixT('initiatorNoTelegram')
						: e instanceof Error && e.message.includes('INITIATOR_NOT_NOTIFIED')
							? prefixT('initiatorNotNotified')
							: prefixT('createFailed'),
			)
		}
	}

	return (
		<div className='space-y-4 p-4'>
			<h1 className='text-xl font-semibold text-foreground'>
				{prefixT('title')}
			</h1>

			<Field>
				<FieldLabel htmlFor='title'>{prefixT('name')}</FieldLabel>
				<Input
					id='title'
					value={title}
					onChange={(e) => setTitle(e.target.value)}
				/>
			</Field>
			<Field>
				<FieldLabel>{prefixT('productPhotos')}</FieldLabel>
				<MultiImageDropzone
					urls={photos.map((photo) => photo.url)}
					onFiles={handlePhotos}
					onRemove={(i) =>
						setPhotos((current) => current.filter((_, idx) => idx !== i))
					}
					uploadingCount={uploadingCount}
				/>
			</Field>
			<Field>
				<FieldLabel>{prefixT('categories')}</FieldLabel>
				{!categories?.length ? (
					<FieldDescription>{prefixT('noCategories')}</FieldDescription>
				) : (
					<div className='flex flex-wrap gap-2'>
						{categories.map((cat) => (
							<Button
								key={cat._id}
								type='button'
								size='sm'
								variant={categoryIds.includes(cat._id) ? 'default' : 'outline'}
								onClick={() => toggleCategory(cat._id)}
							>
								{getLocalizedCategoryName(cat, lang)}
							</Button>
						))}
					</div>
				)}
			</Field>
			<Field>
				<FieldLabel htmlFor='notes'>{prefixT('notes')}</FieldLabel>
				<Textarea
					id='notes'
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
				/>
			</Field>
			<Field>
				<FieldLabel htmlFor='description'>
					{prefixT('buyerDescription')}
				</FieldLabel>
				<Textarea
					id='description'
					value={description}
					onChange={(e) => setDescription(e.target.value)}
				/>
			</Field>
			<Field>
				<FieldLabel htmlFor='storeUrl'>{prefixT('storeLink')}</FieldLabel>
				<Input
					id='storeUrl'
					type='url'
					placeholder='https://...'
					value={storeUrl}
					onChange={(e) => setStoreUrl(e.target.value)}
				/>
			</Field>
			<Field>
				<FieldLabel htmlFor='usd'>{prefixT('wholesalePrice')}</FieldLabel>
				<Input
					id='usd'
					type='number'
					inputMode='decimal'
					value={priceUsd}
					onChange={(e) => {
						priceUsdRef.current = e.target.value
						setPriceUsd(e.target.value)
					}}
					onBlur={() => void reconcileRecommendedPrice()}
				/>
				{priceCalculation && recommendedPriceUzs !== undefined && (
					<FieldDescription
						key={priceCorrectionVersion}
						className={
							priceCorrectionVersion > 0 ? 'price-correction' : undefined
						}
					>
						{prefixT('recommendedPrice', {
							price: t('common.amountUzs', {
								amount: formatUzsAmount(recommendedPriceUzs),
							}),
							rate: String(priceCalculation.rate),
							commission: String(priceCalculation.commissionPercent),
						})}
					</FieldDescription>
				)}
			</Field>
			<Field>
				<FieldLabel htmlFor='uzs'>{prefixT('priceUzs')}</FieldLabel>
				<Input
					id='uzs'
					type='number'
					inputMode='numeric'
					placeholder={
						recommendedPriceUzs !== undefined ? String(recommendedPriceUzs) : ''
					}
					value={priceUzs}
					onChange={(e) => setPriceUzs(e.target.value)}
				/>
			</Field>
			<Field>
				<FieldLabel>{prefixT('inventoryType')}</FieldLabel>
				<div className='flex gap-2'>
					<Button
						type='button'
						size='sm'
						variant={inventoryType === 'slots' ? 'default' : 'outline'}
						onClick={() => setInventoryType('slots')}
					>
						{prefixT('inventoryTypeSlots')}
					</Button>
					<Button
						type='button'
						size='sm'
						variant={inventoryType === 'stock' ? 'default' : 'outline'}
						onClick={() => setInventoryType('stock')}
					>
						{prefixT('inventoryTypeStock')}
					</Button>
				</div>
				<FieldDescription>
					{prefixT('inventoryTypeDescription')}
				</FieldDescription>
			</Field>

			{inventoryType === 'slots' ? (
				<Field>
					<FieldLabel htmlFor='sizes'>{prefixT('sizes')}</FieldLabel>
					<Input
						id='sizes'
						placeholder={prefixT('sizesPlaceholder')}
						value={sizesRaw}
						onChange={(e) => setSizesRaw(e.target.value)}
					/>
					<FieldDescription>{prefixT('sizesDescription')}</FieldDescription>
				</Field>
			) : (
				<Field>
					<FieldLabel>{prefixT('stockItems')}</FieldLabel>
					<div className='space-y-2'>
						{stockRows.map((row) => (
							<div key={row.tempId} className='flex items-center gap-2'>
								<Input
									className='flex-1'
									placeholder={prefixT('stockItemNamePlaceholder')}
									value={row.name}
									onChange={(e) =>
										updateStockRow(row.tempId, { name: e.target.value })
									}
								/>
								<Input
									className='w-24'
									type='number'
									inputMode='numeric'
									min={1}
									placeholder={prefixT('stockItemMax')}
									value={row.maxQuantity}
									onChange={(e) =>
										updateStockRow(row.tempId, { maxQuantity: e.target.value })
									}
								/>
								<Button
									type='button'
									variant='ghost'
									size='icon'
									className='text-destructive hover:bg-destructive/10'
									onClick={() => removeStockRow(row.tempId)}
									disabled={stockRows.length === 1}
									title={prefixT('removeStockItem')}
								>
									<Trash2 className='size-4' />
								</Button>
							</div>
						))}
					</div>
					<Button
						type='button'
						variant='outline'
						size='sm'
						onClick={addStockRow}
					>
						<Plus className='mr-1 size-4' />
						{prefixT('addStockItem')}
					</Button>
					<FieldDescription>{prefixT('stockDescription')}</FieldDescription>
				</Field>
			)}
			<Field>
				<FieldLabel htmlFor='buyerTezId'>{prefixT('buyerTezId')}</FieldLabel>
				<form
					className='flex items-center gap-2'
					onSubmit={(e) => {
						e.preventDefault()
						void lookupBuyer()
					}}
				>
					<Input
						id='buyerTezId'
						value={buyerTezId}
						onChange={(e) => {
							setBuyerTezId(e.target.value.toUpperCase())
							setBuyerNotFound(false)
							setBuyerName(null)
							// An edit invalidates the previous lookup, so the on-blur
							// fallback will run again for the new value.
							lastLookedUpRef.current = null
						}}
						// Fallback only: if the admin never pressed Enter / "Найти", still
						// look up on blur. Skipped automatically when the value was already
						// checked (a found buyer is shown, or the same value already tried).
						onBlur={lookupBuyer}
						className={
							buyerNotFound
								? 'flex-1 border-red-500 focus-visible:ring-red-500'
								: 'flex-1'
						}
					/>
					<Button
						type='submit'
						variant='outline'
						size='sm'
						disabled={lookingUp || !buyerTezId.trim()}
					>
						{lookingUp ? t('common.searching') : t('common.search')}
					</Button>
				</form>
				<FieldDescription>{prefixT('checkBuyer')}</FieldDescription>
				{buyerNotFound && (
					<FieldDescription className='text-red-500'>
						{prefixT('buyerNotFound')}
					</FieldDescription>
				)}
				{buyerName && (
					<FieldDescription>
						{prefixT('buyer', { name: buyerName })}
					</FieldDescription>
				)}
			</Field>

			<Button
				onClick={handleCreate}
				disabled={
					create.isPending ||
					uploadingCount > 0 ||
					!title ||
					photos.length === 0 ||
					usd <= 0 ||
					!inventoryValid ||
					// A typed TezId must resolve to a real buyer before the lot is created.
					(buyerTezId.trim() !== '' && (!buyerName || lookingUp))
				}
			>
				{create.isPending ? prefixT('creating') : prefixT('title')}
			</Button>
		</div>
	)
}
